import React from 'react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/Button';
import { Link } from 'react-router-dom';
import { Palette, Code, Camera, Zap } from 'lucide-react';
const team = [{
  name: 'Rizky Ramadhan',
  role: 'Creative Director',
  image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=800&q=80'
}, {
  name: 'Sarah Wijaya',
  role: 'Lead Designer',
  image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=800&q=80'
}, {
  name: 'Budi Santoso',
  role: 'Tech Lead',
  image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=800&q=80'
}, {
  name: 'Maya Putri',
  role: 'Content Strategist',
  image: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=800&q=80'
}];
const services = [{
  icon: <Palette className="w-8 h-8" data-id="element-270" />,
  title: 'Branding',
  description: 'We build brands that speak louder. Logos, visual identity, and brand strategy that cuts through the noise.'
}, {
  icon: <Code className="w-8 h-8" data-id="element-271" />,
  title: 'Digital',
  description: 'Websites and applications that are fast, responsive, and built with the latest technology.'
}, {
  icon: <Camera className="w-8 h-8" data-id="element-272" />,
  title: 'Content',
  description: 'Photography, copywriting, and art direction that tells your story with authenticity.'
}, {
  icon: <Zap className="w-8 h-8" data-id="element-273" />,
  title: 'Motion',
  description: 'Bringing static designs to life with smooth animations and interactive experiences.'
}];
export function Studio() {
  return <div className="min-h-screen bg-black text-white selection:bg-yellow-400 selection:text-black" data-id="element-274">
      <Navigation data-id="element-275" />

      <main className="pt-32 pb-24" data-id="element-276">
        {/* Hero / About */}
        <section className="px-6 mb-32" data-id="element-277">
          <div className="max-w-7xl mx-auto" data-id="element-278">
            <h1 className="text-6xl md:text-9xl font-black tracking-tighter mb-12" data-id="element-279">
              WE ARE <br data-id="element-280" />
              <span className="text-yellow-400" data-id="element-281">KUJODO</span>
            </h1>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-12" data-id="element-282">
              <div className="text-2xl md:text-3xl font-bold leading-tight" data-id="element-283">
                We are a creative studio obsessed with bold ideas and flawless
                execution. We don't just design; we define.
              </div>
              <div className="text-gray-400 text-lg leading-relaxed space-y-6" data-id="element-284">
                <p data-id="element-285">
                  Founded in Jakarta, KUJODO was born from a desire to break the
                  mold of traditional agencies. We believe that great design
                  shouldn't just look good—it should provoke, inspire, and drive
                  results.
                </p>
                <p data-id="element-286">
                  Our team is a collective of designers, developers, and
                  strategists who share a common goal: to create digital
                  experiences that people actually remember. We work with brands
                  that aren't afraid to stand out.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Services */}
        <section className="px-6 py-24 bg-gray-900/30 border-y border-white/10 mb-32" data-id="element-287">
          <div className="max-w-7xl mx-auto" data-id="element-288">
            <h2 className="text-yellow-400 font-bold tracking-widest uppercase mb-16" data-id="element-289">
              Our Expertise
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12" data-id="element-290">
              {services.map((service, index) => <div key={index} className="group" data-id="element-291">
                  <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mb-6 text-yellow-400 group-hover:bg-yellow-400 group-hover:text-black transition-colors duration-300" data-id="element-292">
                    {service.icon}
                  </div>
                  <h3 className="text-2xl font-bold mb-4" data-id="element-293">{service.title}</h3>
                  <p className="text-gray-400 group-hover:text-gray-300 transition-colors" data-id="element-294">
                    {service.description}
                  </p>
                </div>)}
            </div>
          </div>
        </section>

        {/* Team */}
        <section className="px-6 mb-32" data-id="element-295">
          <div className="max-w-7xl mx-auto" data-id="element-296">
            <div className="flex justify-between items-end mb-16" data-id="element-297">
              <h2 className="text-4xl md:text-6xl font-black uppercase tracking-tighter" data-id="element-298">
                The Team
              </h2>
              <Link to="/contact" data-id="element-299">
                <Button variant="outline" data-id="element-300">Join Us</Button>
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-301">
              {team.map((member, index) => <div key={index} className="group" data-id="element-302">
                  <div className="aspect-[3/4] overflow-hidden mb-6 bg-gray-800 relative" data-id="element-303">
                    <img src={member.image} alt={member.name} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500 scale-100 group-hover:scale-105" data-id="element-304" />
                    <div className="absolute inset-0 bg-yellow-400/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 mix-blend-overlay" data-id="element-305" />
                  </div>
                  <h3 className="text-xl font-bold uppercase tracking-wide mb-1 group-hover:text-yellow-400 transition-colors" data-id="element-306">
                    {member.name}
                  </h3>
                  <p className="text-gray-500 text-sm font-bold uppercase tracking-wider" data-id="element-307">
                    {member.role}
                  </p>
                </div>)}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="px-6" data-id="element-308">
          <div className="max-w-7xl mx-auto bg-yellow-400 text-black p-12 md:p-24 text-center" data-id="element-309">
            <h2 className="text-4xl md:text-7xl font-black uppercase tracking-tighter mb-8" data-id="element-310">
              Have an idea?
            </h2>
            <p className="text-xl md:text-2xl font-bold mb-12 max-w-2xl mx-auto" data-id="element-311">
              Let's turn your vision into a reality. We are currently accepting
              new projects for Q2 2024.
            </p>
            <Link to="/contact" data-id="element-312">
              <button className="bg-black text-white px-10 py-5 font-bold uppercase tracking-widest hover:bg-white hover:text-black transition-colors text-lg" data-id="element-313">
                Start a Project
              </button>
            </Link>
          </div>
        </section>
      </main>

      <Footer data-id="element-314" />
    </div>;
}